import type { ConnectedProviderApi, SessionStatus, SignalSummary } from "./api";

/**
 * The step the server records beside a Microsoft connection when THAT sign-in
 * proved the address (platform#712). Named here only so the demo lanes can
 * mirror the server's definitions; the screens never read it to decide proof,
 * they read `email_proven`.
 */
export const MICROSOFT_EMAIL_PROOF_STEP = "oauth_microsoft_email_proof";

/**
 * Used ONLY when the backend sends no `email_proven` (from before
 * platform#721): the email-step sign-ins such a backend accepts and cannot
 * tell apart per sign-in. Microsoft is here because that backend records no
 * proof per sign-in, so it cannot say which Microsoft sign-ins proved the
 * address; this is also what keeps e2e-stack J1 honest on the platform's main
 * images.
 *
 * ⚠️ Amazon and Uber are NOT here, although that backend's email step accepted
 * them (verified as of 2026-09-29, platform migration 1785024000000): they
 * never prove an email, whatever the backend version (platform#712), and
 * counting them showed a leasing applicant with only an Amazon connection
 * "Your email step is complete". The reading that counts fewer wins.
 *
 * Once platform#721 is live everywhere the field is always present and this
 * list is dead; delete it then.
 */
const PRE_712_EMAIL_PROVIDERS = new Set(["google", "microsoft", "linkedin", "apple"]);

type EmailProofSession = Pick<SessionStatus, "connected_providers">;

/**
 * Did this one connection prove the applicant controls an email address?
 *
 * The server's per-connection `email_proven` decides (platform#721: Google,
 * LinkedIn and Apple by connecting; Microsoft only for a sign-in that proved
 * the address; Amazon, Uber and Facebook never). Without the field, mirror the
 * older backend's email step (see PRE_712_EMAIL_PROVIDERS). Never re-derived
 * from provider names when the server has answered.
 */
export function connectionProvedEmail(
  connection: Pick<ConnectedProviderApi, "provider" | "status" | "email_proven">,
): boolean {
  if (connection.status !== "connected") return false;
  if (typeof connection.email_proven === "boolean") return connection.email_proven;
  return PRE_712_EMAIL_PROVIDERS.has(connection.provider.toLowerCase());
}

/** Did a connected sign-in at this provider prove the address? */
export function signInProvedEmail(session: EmailProofSession | null | undefined, provider: string | undefined): boolean {
  if (!session || !provider) return false;
  return (session.connected_providers ?? []).some(
    (p) => p.provider.toLowerCase() === provider.toLowerCase() && connectionProvedEmail(p),
  );
}

/** Did ANY connected sign-in prove the address? One-time codes are counted separately, via `isOtpEmailSignal`. */
export function anySignInProvedEmail(session: EmailProofSession | null | undefined): boolean {
  if (!session) return false;
  return (session.connected_providers ?? []).some(
    (p) => p.provider.toLowerCase() !== "email" && connectionProvedEmail(p),
  );
}

/**
 * Is this `signals_summary` entry a one-time-code email verification?
 *
 * Exactly `contact_email`. Not `digital_email_history`, which every Google,
 * Microsoft, Amazon, Uber or Apple connection writes and which proves an
 * account's history, not the address; and not `contact_email_disposable`, a
 * quality label.
 */
export function isOtpEmailSignal(signal: Pick<SignalSummary, "key" | "status">): boolean {
  return signal.key.toLowerCase() === "contact_email" && signal.status === "verified";
}
